/**
 * FormulaBuilder – Modal wizard for creating/editing calculated indicators.
 *
 * Steps:
 *   1. Basis        – name, description, unit, category, subcategory, formula_type
 *   2. Formel       – dynamic form depending on formula_type
 *   3. Text-Mapping – optional TeGoVa score-to-label mapping
 *   4. Vorschau     – preview computed results (requires saved indicator)
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  Save,
  FlaskConical,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAdminIndicators } from '../hooks/indicatorQueries'
import {
  useCalculatedIndicators,
  useCreateCalculatedIndicator,
  useUpdateCalculatedIndicator,
  usePreviewCalculatedIndicator,
} from '../hooks/indicatorQueries'
import type {
  CalculatedIndicatorCreate,
  CalculatedIndicatorResponse,
  IndicatorAdminInfo,
  PreviewResult,
} from '../services/indicatorApi'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FormulaBuilderProps {
  editId?: number
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type FormulaType = CalculatedIndicatorCreate['formula_type']

const FORMULA_TYPE_OPTIONS: { value: FormulaType; label: string; description: string }[] = [
  { value: 'arithmetic', label: 'Arithmetisch', description: 'Formel mit Variablen (z.B. A / B * 100)' },
  { value: 'quantile_rank', label: 'Quantil-Rang', description: 'Rang basierend auf einem Indikator' },
  { value: 'weighted_rank', label: 'Gewichteter Rang', description: 'Gewichtete Kombination mehrerer Indikatoren' },
  { value: 'weighted_composite', label: 'Gewichteter Kompositwert', description: 'Gewichtete Kombination berechneter Indikatoren' },
]

const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'municipality', label: 'Gemeinde' },
  { value: 'county', label: 'Kreis' },
  { value: 'government_district', label: 'Regierungsbezirk' },
  { value: 'state', label: 'Bundesland' },
  { value: 'federal', label: 'Bund' },
]

const DEFAULT_TEGOVA: TextMappingEntry[] = [
  { min: 90, max: 100, label: 'Herausragend' },
  { min: 80, max: 89.999, label: 'Ausgezeichnet' },
  { min: 70, max: 79.999, label: 'Sehr gut' },
  { min: 60, max: 69.999, label: 'Gut' },
  { min: 50, max: 59.999, label: 'Durchschnittlich' },
  { min: 40, max: 49.999, label: 'Ausreichend' },
  { min: 30, max: 39.999, label: 'Maessig' },
  { min: 20, max: 29.999, label: 'Schwach' },
  { min: 10, max: 19.999, label: 'Schlecht' },
  { min: 0, max: 9.999, label: 'Unzureichend' },
]

const STEP_LABELS = ['Basis', 'Formel', 'Text-Mapping', 'Vorschau']

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface TextMappingEntry {
  min: number
  max: number
  label: string
}

type ComponentType = 'direct' | 'average_of' | 'weighted_group'

interface WeightedRankComponent {
  type: ComponentType
  indicatorId: number | null
  direction: 'desc' | 'asc'
  weight: string // kept as string for input handling
  // For average_of / weighted_group sub-items
  subItems: {
    indicatorId: number | null
    direction: 'desc' | 'asc'
    weight: string // only used for weighted_group
  }[]
}

interface ArithmeticVariable {
  key: string
  indicatorId: number | null
}

interface FormState {
  name: string
  description: string
  unit: string
  category: string
  subcategory: string
  formulaType: FormulaType
  entityTypes: string[]

  // arithmetic
  arithmeticVars: ArithmeticVariable[]
  arithmeticExpression: string

  // quantile_rank
  quantileIndicatorId: number | null
  quantileDirection: 'desc' | 'asc'

  // weighted_rank
  weightedRankComponents: WeightedRankComponent[]

  // weighted_composite
  compositeComponents: { calcIndicatorId: number | null; weight: string }[]

  // text mapping
  textMappingEnabled: boolean
  textMappingEntries: TextMappingEntry[]
}

function initialFormState(): FormState {
  return {
    name: '',
    description: '',
    unit: '',
    category: '',
    subcategory: '',
    formulaType: 'arithmetic',
    entityTypes: ['municipality'],

    arithmeticVars: [
      { key: 'A', indicatorId: null },
      { key: 'B', indicatorId: null },
    ],
    arithmeticExpression: 'A / B * 100',

    quantileIndicatorId: null,
    quantileDirection: 'desc',

    weightedRankComponents: [makeEmptyComponent()],

    compositeComponents: [{ calcIndicatorId: null, weight: '100' }],

    textMappingEnabled: false,
    textMappingEntries: DEFAULT_TEGOVA.map((e) => ({ ...e })),
  }
}

function makeEmptyComponent(): WeightedRankComponent {
  return { type: 'direct', indicatorId: null, direction: 'desc', weight: '', subItems: [] }
}

// ---------------------------------------------------------------------------
// Helper: parse existing CalculatedIndicatorResponse into FormState
// ---------------------------------------------------------------------------

function parseExisting(ci: CalculatedIndicatorResponse): FormState {
  const state = initialFormState()
  state.name = ci.name
  state.description = ci.description ?? ''
  state.unit = ci.unit ?? ''
  state.category = ci.category ?? ''
  state.subcategory = ci.subcategory ?? ''
  state.formulaType = ci.formula_type as FormulaType
  state.entityTypes = ci.entity_types.length > 0 ? ci.entity_types : ['municipality']

  const def = ci.formula_definition

  if (ci.formula_type === 'arithmetic') {
    const vars = (def.variables ?? {}) as Record<string, { indicator_id: number }>
    state.arithmeticVars = Object.entries(vars).map(([key, v]) => ({
      key,
      indicatorId: v.indicator_id,
    }))
    if (state.arithmeticVars.length === 0) {
      state.arithmeticVars = [
        { key: 'A', indicatorId: null },
        { key: 'B', indicatorId: null },
      ]
    }
    state.arithmeticExpression = (def.expression as string) ?? 'A / B * 100'
  }

  if (ci.formula_type === 'quantile_rank') {
    state.quantileIndicatorId = (def.indicator_id as number) ?? null
    state.quantileDirection = ((def.direction as string) ?? 'desc') as 'desc' | 'asc'
  }

  if (ci.formula_type === 'weighted_rank') {
    const comps = (def.components ?? []) as Record<string, unknown>[]
    state.weightedRankComponents = comps.map((c) => {
      if (c.average_of) {
        const subs = (c.average_of as { indicator_id: number; direction: string }[])
        return {
          type: 'average_of' as ComponentType,
          indicatorId: null,
          direction: 'desc' as const,
          weight: String(((c.weight as number) ?? 0) * 100),
          subItems: subs.map((s) => ({
            indicatorId: s.indicator_id,
            direction: (s.direction ?? 'desc') as 'desc' | 'asc',
            weight: '',
          })),
        }
      }
      if (c.weighted_group) {
        const subs = (c.weighted_group as { indicator_id: number; direction: string; weight: number }[])
        return {
          type: 'weighted_group' as ComponentType,
          indicatorId: null,
          direction: 'desc' as const,
          weight: String(((c.weight as number) ?? 0) * 100),
          subItems: subs.map((s) => ({
            indicatorId: s.indicator_id,
            direction: (s.direction ?? 'desc') as 'desc' | 'asc',
            weight: String((s.weight ?? 0) * 100),
          })),
        }
      }
      return {
        type: 'direct' as ComponentType,
        indicatorId: (c.indicator_id as number) ?? null,
        direction: ((c.direction as string) ?? 'desc') as 'desc' | 'asc',
        weight: String(((c.weight as number) ?? 0) * 100),
        subItems: [],
      }
    })
    if (state.weightedRankComponents.length === 0) {
      state.weightedRankComponents = [makeEmptyComponent()]
    }
  }

  if (ci.formula_type === 'weighted_composite') {
    const comps = (def.components ?? []) as { indicator_id: number; weight: number }[]
    state.compositeComponents = comps.map((c) => ({
      calcIndicatorId: c.indicator_id,
      weight: String((c.weight ?? 0) * 100),
    }))
    if (state.compositeComponents.length === 0) {
      state.compositeComponents = [{ calcIndicatorId: null, weight: '100' }]
    }
  }

  // text mapping
  if (ci.text_mapping && typeof ci.text_mapping === 'object') {
    const tm = ci.text_mapping as { enabled?: boolean; ranges?: TextMappingEntry[] }
    if (tm.enabled) {
      state.textMappingEnabled = true
      if (Array.isArray(tm.ranges) && tm.ranges.length > 0) {
        state.textMappingEntries = tm.ranges.map((r) => ({ min: r.min, max: r.max, label: r.label }))
      }
    }
  }

  return state
}

// ---------------------------------------------------------------------------
// Helper: FormState -> CalculatedIndicatorCreate body
// ---------------------------------------------------------------------------

function buildBody(form: FormState): CalculatedIndicatorCreate {
  let formulaDefinition: Record<string, unknown> = {}

  if (form.formulaType === 'arithmetic') {
    const variables: Record<string, { indicator_id: number }> = {}
    for (const v of form.arithmeticVars) {
      if (v.indicatorId != null) {
        variables[v.key] = { indicator_id: v.indicatorId }
      }
    }
    formulaDefinition = { expression: form.arithmeticExpression, variables }
  }

  if (form.formulaType === 'quantile_rank') {
    formulaDefinition = {
      indicator_id: form.quantileIndicatorId,
      direction: form.quantileDirection,
    }
  }

  if (form.formulaType === 'weighted_rank') {
    formulaDefinition = {
      components: form.weightedRankComponents.map((c) => {
        const w = parseFloat(c.weight) / 100
        if (c.type === 'average_of') {
          return {
            average_of: c.subItems.map((s) => ({
              indicator_id: s.indicatorId,
              direction: s.direction,
            })),
            weight: w,
          }
        }
        if (c.type === 'weighted_group') {
          return {
            weighted_group: c.subItems.map((s) => ({
              indicator_id: s.indicatorId,
              direction: s.direction,
              weight: parseFloat(s.weight) / 100,
            })),
            weight: w,
          }
        }
        return {
          indicator_id: c.indicatorId,
          direction: c.direction,
          weight: w,
        }
      }),
    }
  }

  if (form.formulaType === 'weighted_composite') {
    formulaDefinition = {
      components: form.compositeComponents.map((c) => ({
        indicator_id: c.calcIndicatorId,
        weight: parseFloat(c.weight) / 100,
      })),
    }
  }

  const body: CalculatedIndicatorCreate = {
    name: form.name,
    formula_type: form.formulaType,
    formula_definition: formulaDefinition,
    entity_types: form.entityTypes,
  }

  if (form.description) body.description = form.description
  if (form.unit) body.unit = form.unit
  if (form.category) body.category = form.category
  if (form.subcategory) body.subcategory = form.subcategory

  if (form.textMappingEnabled) {
    body.text_mapping = {
      enabled: true,
      ranges: form.textMappingEntries,
    }
  }

  return body
}

// ---------------------------------------------------------------------------
// Indicator search/select helper component
// ---------------------------------------------------------------------------

function IndicatorSelect({
  indicators,
  value,
  onChange,
  placeholder,
  filterText,
  onFilterChange,
}: {
  indicators: IndicatorAdminInfo[]
  value: number | null
  onChange: (id: number | null) => void
  placeholder?: string
  filterText: string
  onFilterChange: (v: string) => void
}) {
  const filtered = useMemo(() => {
    if (!filterText) return indicators
    const lower = filterText.toLowerCase()
    return indicators.filter(
      (i) =>
        i.name.toLowerCase().includes(lower) ||
        i.code.toLowerCase().includes(lower)
    )
  }, [indicators, filterText])

  return (
    <div className="space-y-1">
      <input
        type="text"
        value={filterText}
        onChange={(e) => onFilterChange(e.target.value)}
        placeholder="Indikator filtern..."
        className="w-full px-2 py-1 text-xs border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
      />
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="w-full px-2 py-1.5 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      >
        <option value="">{placeholder ?? '-- Indikator waehlen --'}</option>
        {filtered.map((ind) => (
          <option key={ind.id} value={ind.id}>
            {ind.name} ({ind.code})
          </option>
        ))}
      </select>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FormulaBuilder({ editId, onClose, onSaved }: FormulaBuilderProps) {
  const [step, setStep] = useState(0)
  const [form, setForm] = useState<FormState>(initialFormState())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Indicator filter text states (separate per dropdown to avoid interference)
  const [filterTexts, setFilterTexts] = useState<Record<string, string>>({})

  const setFilterText = useCallback((key: string, value: string) => {
    setFilterTexts((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Load all indicators for dropdowns
  const { data: indicatorData } = useAdminIndicators({ limit: 1000 })
  const allIndicators = indicatorData?.items ?? []

  // Load calculated indicators for edit mode and weighted_composite
  const { data: calcData } = useCalculatedIndicators()
  const allCalculated = calcData?.items ?? []

  // For edit mode, populate form from existing data
  useEffect(() => {
    if (editId && allCalculated.length > 0) {
      const existing = allCalculated.find((ci) => ci.id === editId)
      if (existing) {
        setForm(parseExisting(existing))
      }
    }
  }, [editId, allCalculated])

  // Mutations
  const createMut = useCreateCalculatedIndicator()
  const updateMut = useUpdateCalculatedIndicator()

  // Preview
  const [previewEntityType, setPreviewEntityType] = useState('municipality')
  const previewMut = usePreviewCalculatedIndicator(editId ?? 0)
  const [previewResults, setPreviewResults] = useState<PreviewResult[]>([])
  const [previewErrors, setPreviewErrors] = useState<string[]>([])

  // ---- Form updater helpers ----
  function updateForm<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleEntityType(et: string) {
    setForm((prev) => {
      const types = prev.entityTypes.includes(et)
        ? prev.entityTypes.filter((t) => t !== et)
        : [...prev.entityTypes, et]
      return { ...prev, entityTypes: types.length > 0 ? types : prev.entityTypes }
    })
  }

  // ---- Save ----
  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    try {
      const body = buildBody(form)
      if (editId) {
        await updateMut.mutateAsync({ id: editId, body })
      } else {
        await createMut.mutateAsync(body)
      }
      onSaved()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Speichern fehlgeschlagen'
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  // ---- Preview ----
  async function handlePreview() {
    if (!editId) return
    setPreviewResults([])
    setPreviewErrors([])
    try {
      const res = await previewMut.mutateAsync({ entityType: previewEntityType, limit: 10 })
      setPreviewResults(res.results)
      setPreviewErrors(res.errors)
    } catch (err: unknown) {
      setPreviewErrors([err instanceof Error ? err.message : 'Vorschau fehlgeschlagen'])
    }
  }

  // ---- Validation (simple) ----
  const canProceed = step === 0
    ? form.name.trim().length > 0 && form.entityTypes.length > 0
    : true

  // Weight sum helpers
  const weightedRankSum = form.weightedRankComponents.reduce(
    (s, c) => s + (parseFloat(c.weight) || 0), 0
  )
  const compositeSum = form.compositeComponents.reduce(
    (s, c) => s + (parseFloat(c.weight) || 0), 0
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-gut8 shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-gut8-light">
          <h2 className="text-lg font-bold text-gut8-anthracite">
            {editId ? 'Berechneten Indikator bearbeiten' : 'Berechneten Indikator erstellen'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-gut8 hover:bg-gut8-bg-muted transition-colors">
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b border-border-gut8-light bg-gut8-bg-muted">
          <div className="flex items-center gap-2 text-xs">
            {STEP_LABELS.map((label, idx) => (
              <div key={label} className="flex items-center gap-2">
                {idx > 0 && <ChevronRight className="w-3 h-3 text-text-muted" />}
                <button
                  onClick={() => setStep(idx)}
                  className={cn(
                    'px-2 py-1 rounded-gut8 font-medium transition-colors',
                    idx === step
                      ? 'bg-primary text-white'
                      : idx < step
                        ? 'text-primary hover:bg-primary-subtle'
                        : 'text-text-muted'
                  )}
                >
                  {idx + 1}. {label}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Body – scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {step === 0 && (
            <StepBasis
              form={form}
              updateForm={updateForm}
              toggleEntityType={toggleEntityType}
            />
          )}
          {step === 1 && (
            <StepFormula
              form={form}
              updateForm={updateForm}
              allIndicators={allIndicators}
              allCalculated={allCalculated}
              filterTexts={filterTexts}
              setFilterText={setFilterText}
              weightedRankSum={weightedRankSum}
              compositeSum={compositeSum}
            />
          )}
          {step === 2 && (
            <StepTextMapping form={form} updateForm={updateForm} />
          )}
          {step === 3 && (
            <StepPreview
              editId={editId}
              previewEntityType={previewEntityType}
              setPreviewEntityType={setPreviewEntityType}
              onPreview={handlePreview}
              isPreviewing={previewMut.isPending}
              results={previewResults}
              errors={previewErrors}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-gut8-light bg-gut8-bg-muted">
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white hover:bg-gut8-bg-muted transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Zurueck
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {saveError && (
              <span className="text-xs text-red-600 mr-2">{saveError}</span>
            )}

            <button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-white rounded-gut8 hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Speichern
            </button>

            {step < STEP_LABELS.length - 1 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-gut8-anthracite text-white rounded-gut8 hover:bg-gut8-anthracite/90 transition-colors disabled:opacity-50"
              >
                Weiter
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// Step 1: Basis
// ===========================================================================

function StepBasis({
  form,
  updateForm,
  toggleEntityType,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  toggleEntityType: (et: string) => void
}) {
  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => updateForm('name', e.target.value)}
          placeholder="z.B. Kaufkraft-Index"
          className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-1">Beschreibung</label>
        <textarea
          value={form.description}
          onChange={(e) => updateForm('description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
        />
      </div>

      {/* Unit + Category + Subcategory */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gut8-anthracite mb-1">Einheit</label>
          <input
            type="text"
            value={form.unit}
            onChange={(e) => updateForm('unit', e.target.value)}
            placeholder="z.B. Index, %, EUR"
            className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gut8-anthracite mb-1">Kategorie</label>
          <input
            type="text"
            value={form.category}
            onChange={(e) => updateForm('category', e.target.value)}
            placeholder="z.B. Wirtschaft"
            className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gut8-anthracite mb-1">Unterkategorie</label>
          <input
            type="text"
            value={form.subcategory}
            onChange={(e) => updateForm('subcategory', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
      </div>

      {/* Formula type */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-2">
          Formeltyp <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {FORMULA_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateForm('formulaType', opt.value)}
              className={cn(
                'text-left p-3 rounded-gut8 border transition-colors',
                form.formulaType === opt.value
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                  : 'border-border-gut8-light hover:bg-gut8-bg-muted'
              )}
            >
              <div className="text-sm font-medium text-gut8-anthracite">{opt.label}</div>
              <div className="text-xs text-text-muted mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Entity types */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-2">
          Entity-Types <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {ENTITY_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggleEntityType(opt.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-gut8 border transition-colors',
                form.entityTypes.includes(opt.value)
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border-gut8-light text-text-muted hover:bg-gut8-bg-muted'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// Step 2: Formula
// ===========================================================================

function StepFormula({
  form,
  updateForm,
  allIndicators,
  allCalculated,
  filterTexts,
  setFilterText,
  weightedRankSum,
  compositeSum,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  allIndicators: IndicatorAdminInfo[]
  allCalculated: CalculatedIndicatorResponse[]
  filterTexts: Record<string, string>
  setFilterText: (key: string, value: string) => void
  weightedRankSum: number
  compositeSum: number
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold text-gut8-anthracite">
        Formel: {FORMULA_TYPE_OPTIONS.find((o) => o.value === form.formulaType)?.label}
      </h3>

      {form.formulaType === 'arithmetic' && (
        <ArithmeticForm
          form={form}
          updateForm={updateForm}
          allIndicators={allIndicators}
          filterTexts={filterTexts}
          setFilterText={setFilterText}
        />
      )}

      {form.formulaType === 'quantile_rank' && (
        <QuantileRankForm
          form={form}
          updateForm={updateForm}
          allIndicators={allIndicators}
          filterTexts={filterTexts}
          setFilterText={setFilterText}
        />
      )}

      {form.formulaType === 'weighted_rank' && (
        <WeightedRankForm
          form={form}
          updateForm={updateForm}
          allIndicators={allIndicators}
          filterTexts={filterTexts}
          setFilterText={setFilterText}
          weightSum={weightedRankSum}
        />
      )}

      {form.formulaType === 'weighted_composite' && (
        <WeightedCompositeForm
          form={form}
          updateForm={updateForm}
          allCalculated={allCalculated}
          filterTexts={filterTexts}
          setFilterText={setFilterText}
          weightSum={compositeSum}
        />
      )}
    </div>
  )
}

// ---- Arithmetic ----

function ArithmeticForm({
  form,
  updateForm,
  allIndicators,
  filterTexts,
  setFilterText,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  allIndicators: IndicatorAdminInfo[]
  filterTexts: Record<string, string>
  setFilterText: (key: string, value: string) => void
}) {
  function setVar(idx: number, field: keyof ArithmeticVariable, value: string | number | null) {
    const vars = [...form.arithmeticVars]
    vars[idx] = { ...vars[idx], [field]: value }
    updateForm('arithmeticVars', vars)
  }

  function addVar() {
    const usedKeys = new Set(form.arithmeticVars.map((v) => v.key))
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const nextKey = alphabet.split('').find((c) => !usedKeys.has(c)) ?? 'X'
    updateForm('arithmeticVars', [...form.arithmeticVars, { key: nextKey, indicatorId: null }])
  }

  function removeVar(idx: number) {
    if (form.arithmeticVars.length <= 1) return
    updateForm('arithmeticVars', form.arithmeticVars.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-4">
      {/* Variables */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-2">Variablen-Zuordnung</label>
        <div className="space-y-2">
          {form.arithmeticVars.map((v, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="w-16 flex-shrink-0">
                <input
                  type="text"
                  value={v.key}
                  onChange={(e) => setVar(idx, 'key', e.target.value.toUpperCase())}
                  maxLength={2}
                  className="w-full px-2 py-1.5 text-sm text-center font-mono font-bold border border-border-gut8-medium rounded-gut8 bg-gut8-bg-muted"
                />
              </div>
              <span className="py-1.5 text-sm text-text-muted">=</span>
              <div className="flex-1">
                <IndicatorSelect
                  indicators={allIndicators}
                  value={v.indicatorId}
                  onChange={(id) => setVar(idx, 'indicatorId', id)}
                  filterText={filterTexts[`arith_${idx}`] ?? ''}
                  onFilterChange={(val) => setFilterText(`arith_${idx}`, val)}
                />
              </div>
              <button
                onClick={() => removeVar(idx)}
                disabled={form.arithmeticVars.length <= 1}
                className="p-1.5 rounded-gut8 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addVar}
          className="mt-2 flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary-subtle rounded-gut8 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Variable hinzufuegen
        </button>
      </div>

      {/* Expression */}
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-1">Ausdruck</label>
        <input
          type="text"
          value={form.arithmeticExpression}
          onChange={(e) => updateForm('arithmeticExpression', e.target.value)}
          placeholder="z.B. A / B * 100"
          className="w-full px-3 py-2 text-sm font-mono border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        <p className="text-xs text-text-muted mt-1">
          Verwenden Sie die oben definierten Variablen. Operatoren: + - * / ( )
        </p>
      </div>
    </div>
  )
}

// ---- Quantile Rank ----

function QuantileRankForm({
  form,
  updateForm,
  allIndicators,
  filterTexts,
  setFilterText,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  allIndicators: IndicatorAdminInfo[]
  filterTexts: Record<string, string>
  setFilterText: (key: string, value: string) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-1">Indikator</label>
        <IndicatorSelect
          indicators={allIndicators}
          value={form.quantileIndicatorId}
          onChange={(id) => updateForm('quantileIndicatorId', id)}
          filterText={filterTexts['quantile'] ?? ''}
          onFilterChange={(val) => setFilterText('quantile', val)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gut8-anthracite mb-2">Richtung</label>
        <div className="flex gap-3">
          <button
            onClick={() => updateForm('quantileDirection', 'desc')}
            className={cn(
              'flex-1 px-3 py-2 text-sm rounded-gut8 border transition-colors text-center',
              form.quantileDirection === 'desc'
                ? 'border-primary bg-primary/5 text-primary font-medium'
                : 'border-border-gut8-light text-text-muted hover:bg-gut8-bg-muted'
            )}
          >
            Absteigend (hoeher ist besser)
          </button>
          <button
            onClick={() => updateForm('quantileDirection', 'asc')}
            className={cn(
              'flex-1 px-3 py-2 text-sm rounded-gut8 border transition-colors text-center',
              form.quantileDirection === 'asc'
                ? 'border-primary bg-primary/5 text-primary font-medium'
                : 'border-border-gut8-light text-text-muted hover:bg-gut8-bg-muted'
            )}
          >
            Aufsteigend (niedriger ist besser)
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- Weighted Rank ----

function WeightedRankForm({
  form,
  updateForm,
  allIndicators,
  filterTexts,
  setFilterText,
  weightSum,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  allIndicators: IndicatorAdminInfo[]
  filterTexts: Record<string, string>
  setFilterText: (key: string, value: string) => void
  weightSum: number
}) {
  const components = form.weightedRankComponents

  function setComponent(idx: number, comp: WeightedRankComponent) {
    const next = [...components]
    next[idx] = comp
    updateForm('weightedRankComponents', next)
  }

  function addComponent() {
    updateForm('weightedRankComponents', [...components, makeEmptyComponent()])
  }

  function removeComponent(idx: number) {
    if (components.length <= 1) return
    updateForm('weightedRankComponents', components.filter((_, i) => i !== idx))
  }

  function addSubItem(compIdx: number) {
    const comp = { ...components[compIdx] }
    comp.subItems = [...comp.subItems, { indicatorId: null, direction: 'desc' as const, weight: '' }]
    setComponent(compIdx, comp)
  }

  function removeSubItem(compIdx: number, subIdx: number) {
    const comp = { ...components[compIdx] }
    comp.subItems = comp.subItems.filter((_, i) => i !== subIdx)
    setComponent(compIdx, comp)
  }

  function updateSubItem(compIdx: number, subIdx: number, field: string, value: unknown) {
    const comp = { ...components[compIdx] }
    comp.subItems = comp.subItems.map((s, i) =>
      i === subIdx ? { ...s, [field]: value } : s
    )
    setComponent(compIdx, comp)
  }

  return (
    <div className="space-y-3">
      {/* Weight sum indicator */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-gut8 text-sm',
        Math.abs(weightSum - 100) < 0.01
          ? 'bg-green-50 text-green-700'
          : 'bg-amber-50 text-amber-700'
      )}>
        {Math.abs(weightSum - 100) >= 0.01 && <AlertTriangle className="w-4 h-4" />}
        Gewichtssumme: {weightSum.toFixed(1)}%
        {Math.abs(weightSum - 100) >= 0.01 && ' (sollte 100% sein)'}
      </div>

      {components.map((comp, idx) => (
        <div key={idx} className="p-3 border border-border-gut8-light rounded-gut8 bg-gut8-bg-muted/50 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-text-muted w-6">#{idx + 1}</span>

            {/* Type selector */}
            <select
              value={comp.type}
              onChange={(e) => {
                const type = e.target.value as ComponentType
                const updated = { ...comp, type }
                if (type !== 'direct' && comp.subItems.length === 0) {
                  updated.subItems = [{ indicatorId: null, direction: 'desc', weight: '' }]
                }
                if (type === 'direct') {
                  updated.subItems = []
                }
                setComponent(idx, updated)
              }}
              className="px-2 py-1 text-xs border border-border-gut8-medium rounded-gut8 bg-white"
            >
              <option value="direct">Direkt</option>
              <option value="average_of">Mittelwert (average_of)</option>
              <option value="weighted_group">Gewichtete Gruppe</option>
            </select>

            {/* Weight */}
            <div className="flex items-center gap-1 ml-auto">
              <input
                type="number"
                value={comp.weight}
                onChange={(e) => setComponent(idx, { ...comp, weight: e.target.value })}
                placeholder="0"
                min={0}
                max={100}
                step={0.1}
                className="w-20 px-2 py-1 text-sm text-right border border-border-gut8-medium rounded-gut8 bg-white"
              />
              <span className="text-xs text-text-muted">%</span>
            </div>

            <button
              onClick={() => removeComponent(idx)}
              disabled={components.length <= 1}
              className="p-1 rounded-gut8 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Direct: single indicator + direction */}
          {comp.type === 'direct' && (
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <IndicatorSelect
                  indicators={allIndicators}
                  value={comp.indicatorId}
                  onChange={(id) => setComponent(idx, { ...comp, indicatorId: id })}
                  filterText={filterTexts[`wr_${idx}`] ?? ''}
                  onFilterChange={(val) => setFilterText(`wr_${idx}`, val)}
                />
              </div>
              <DirectionToggle
                value={comp.direction}
                onChange={(dir) => setComponent(idx, { ...comp, direction: dir })}
              />
            </div>
          )}

          {/* average_of / weighted_group: sub-items */}
          {(comp.type === 'average_of' || comp.type === 'weighted_group') && (
            <div className="pl-4 border-l-2 border-border-gut8-light space-y-2">
              {comp.subItems.map((sub, sIdx) => (
                <div key={sIdx} className="flex items-start gap-2">
                  <div className="flex-1">
                    <IndicatorSelect
                      indicators={allIndicators}
                      value={sub.indicatorId}
                      onChange={(id) => updateSubItem(idx, sIdx, 'indicatorId', id)}
                      filterText={filterTexts[`wr_${idx}_${sIdx}`] ?? ''}
                      onFilterChange={(val) => setFilterText(`wr_${idx}_${sIdx}`, val)}
                    />
                  </div>
                  <DirectionToggle
                    value={sub.direction}
                    onChange={(dir) => updateSubItem(idx, sIdx, 'direction', dir)}
                  />
                  {comp.type === 'weighted_group' && (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={sub.weight}
                        onChange={(e) => updateSubItem(idx, sIdx, 'weight', e.target.value)}
                        placeholder="0"
                        min={0}
                        max={100}
                        step={1}
                        className="w-16 px-2 py-1 text-xs text-right border border-border-gut8-medium rounded-gut8 bg-white"
                      />
                      <span className="text-xs text-text-muted">%</span>
                    </div>
                  )}
                  <button
                    onClick={() => removeSubItem(idx, sIdx)}
                    className="p-1 rounded-gut8 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addSubItem(idx)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary-subtle rounded-gut8 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Sub-Indikator hinzufuegen
              </button>
            </div>
          )}
        </div>
      ))}

      <button
        onClick={addComponent}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary border border-dashed border-primary/40 rounded-gut8 hover:bg-primary-subtle transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Komponente hinzufuegen
      </button>
    </div>
  )
}

// ---- Direction toggle small ----

function DirectionToggle({
  value,
  onChange,
}: {
  value: 'desc' | 'asc'
  onChange: (v: 'desc' | 'asc') => void
}) {
  return (
    <button
      onClick={() => onChange(value === 'desc' ? 'asc' : 'desc')}
      className={cn(
        'flex-shrink-0 px-2 py-1.5 text-xs rounded-gut8 border transition-colors whitespace-nowrap',
        value === 'desc'
          ? 'bg-green-50 border-green-300 text-green-700'
          : 'bg-blue-50 border-blue-300 text-blue-700'
      )}
      title={value === 'desc' ? 'Hoeher ist besser' : 'Niedriger ist besser'}
    >
      {value === 'desc' ? '\u2191 hoeher' : '\u2193 niedriger'}
    </button>
  )
}

// ---- Weighted Composite ----

function WeightedCompositeForm({
  form,
  updateForm,
  allCalculated,
  filterTexts,
  setFilterText,
  weightSum,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  allCalculated: CalculatedIndicatorResponse[]
  filterTexts: Record<string, string>
  setFilterText: (key: string, value: string) => void
  weightSum: number
}) {
  const comps = form.compositeComponents

  function setComp(idx: number, field: string, value: unknown) {
    const next = comps.map((c, i) => (i === idx ? { ...c, [field]: value } : c))
    updateForm('compositeComponents', next)
  }

  function addComp() {
    updateForm('compositeComponents', [...comps, { calcIndicatorId: null, weight: '' }])
  }

  function removeComp(idx: number) {
    if (comps.length <= 1) return
    updateForm('compositeComponents', comps.filter((_, i) => i !== idx))
  }

  // Filter calculated indicators — for composite, only show calculated indicators (not self)
  const calcOptions = allCalculated.filter((ci) => ci.id !== form.compositeComponents[0]?.calcIndicatorId)

  return (
    <div className="space-y-3">
      {/* Weight sum indicator */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-gut8 text-sm',
        Math.abs(weightSum - 100) < 0.01
          ? 'bg-green-50 text-green-700'
          : 'bg-amber-50 text-amber-700'
      )}>
        {Math.abs(weightSum - 100) >= 0.01 && <AlertTriangle className="w-4 h-4" />}
        Gewichtssumme: {weightSum.toFixed(1)}%
        {Math.abs(weightSum - 100) >= 0.01 && ' (sollte 100% sein)'}
      </div>

      <p className="text-xs text-text-muted">
        Waehlen Sie andere berechnete Indikatoren als Basis-Komponenten.
      </p>

      {comps.map((comp, idx) => (
        <div key={idx} className="flex items-start gap-2 p-2 border border-border-gut8-light rounded-gut8 bg-gut8-bg-muted/50">
          <div className="flex-1">
            <input
              type="text"
              value={filterTexts[`comp_${idx}`] ?? ''}
              onChange={(e) => setFilterText(`comp_${idx}`, e.target.value)}
              placeholder="Berechneten Indikator filtern..."
              className="w-full px-2 py-1 text-xs border border-border-gut8-medium rounded-gut8 bg-white mb-1 focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            <select
              value={comp.calcIndicatorId ?? ''}
              onChange={(e) => setComp(idx, 'calcIndicatorId', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-2 py-1.5 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">-- Berechneter Indikator --</option>
              {calcOptions
                .filter((ci) => {
                  const ft = (filterTexts[`comp_${idx}`] ?? '').toLowerCase()
                  if (!ft) return true
                  return ci.name.toLowerCase().includes(ft)
                })
                .map((ci) => (
                  <option key={ci.id} value={ci.indicator_id}>
                    {ci.name} ({ci.formula_type})
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={comp.weight}
              onChange={(e) => setComp(idx, 'weight', e.target.value)}
              placeholder="0"
              min={0}
              max={100}
              step={0.1}
              className="w-20 px-2 py-1.5 text-sm text-right border border-border-gut8-medium rounded-gut8 bg-white"
            />
            <span className="text-xs text-text-muted">%</span>
          </div>
          <button
            onClick={() => removeComp(idx)}
            disabled={comps.length <= 1}
            className="p-1.5 rounded-gut8 text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      <button
        onClick={addComp}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary border border-dashed border-primary/40 rounded-gut8 hover:bg-primary-subtle transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        Komponente hinzufuegen
      </button>
    </div>
  )
}

// ===========================================================================
// Step 3: Text Mapping
// ===========================================================================

function StepTextMapping({
  form,
  updateForm,
}: {
  form: FormState
  updateForm: <K extends keyof FormState>(key: K, value: FormState[K]) => void
}) {
  const entries = form.textMappingEntries

  function setEntry(idx: number, field: keyof TextMappingEntry, value: string | number) {
    const next = entries.map((e, i) =>
      i === idx ? { ...e, [field]: value } : e
    )
    updateForm('textMappingEntries', next)
  }

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => updateForm('textMappingEnabled', !form.textMappingEnabled)}
          className={cn(
            'w-10 h-6 rounded-full transition-colors relative inline-block',
            form.textMappingEnabled ? 'bg-green-500' : 'bg-gray-300'
          )}
        >
          <span
            className={cn(
              'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
              form.textMappingEnabled ? 'left-5' : 'left-1'
            )}
          />
        </button>
        <span className="text-sm font-medium text-gut8-anthracite">
          TeGoVa Text-Mapping aktivieren
        </span>
      </div>

      {form.textMappingEnabled && (
        <div className="bg-white border border-border-gut8-light rounded-gut8 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gut8-bg-muted border-b border-border-gut8-light">
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">Von</th>
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">Bis</th>
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">Bezeichnung</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr key={idx} className="border-b border-border-gut8-light last:border-b-0">
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      value={entry.min}
                      onChange={(e) => setEntry(idx, 'min', parseFloat(e.target.value) || 0)}
                      step={0.001}
                      className="w-24 px-2 py-1 text-sm border border-border-gut8-medium rounded-gut8 bg-white text-right"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      value={entry.max}
                      onChange={(e) => setEntry(idx, 'max', parseFloat(e.target.value) || 0)}
                      step={0.001}
                      className="w-24 px-2 py-1 text-sm border border-border-gut8-medium rounded-gut8 bg-white text-right"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="text"
                      value={entry.label}
                      onChange={(e) => setEntry(idx, 'label', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-border-gut8-medium rounded-gut8 bg-white"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!form.textMappingEnabled && (
        <p className="text-sm text-text-muted">
          Wenn aktiviert, werden Score-Werte (0-100) automatisch in Textlabel umgewandelt (z.B. &quot;Gut&quot;, &quot;Durchschnittlich&quot;).
        </p>
      )}
    </div>
  )
}

// ===========================================================================
// Step 4: Preview
// ===========================================================================

function StepPreview({
  editId,
  previewEntityType,
  setPreviewEntityType,
  onPreview,
  isPreviewing,
  results,
  errors,
}: {
  editId?: number
  previewEntityType: string
  setPreviewEntityType: (v: string) => void
  onPreview: () => void
  isPreviewing: boolean
  results: PreviewResult[]
  errors: string[]
}) {
  if (!editId) {
    return (
      <div className="bg-gut8-bg-muted rounded-gut8 border border-border-gut8-light p-8 text-center">
        <FlaskConical className="w-8 h-8 text-text-muted mx-auto mb-3" />
        <p className="text-sm text-text-muted">
          Bitte zuerst speichern, um die Vorschau zu nutzen.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gut8-anthracite mb-1">Entity-Typ</label>
          <select
            value={previewEntityType}
            onChange={(e) => setPreviewEntityType(e.target.value)}
            className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={onPreview}
          disabled={isPreviewing}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-white rounded-gut8 hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {isPreviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          Testen
        </button>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-gut8 p-3">
          {errors.map((err, i) => (
            <p key={i} className="text-sm text-red-700">{err}</p>
          ))}
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="bg-white border border-border-gut8-light rounded-gut8 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gut8-bg-muted border-b border-border-gut8-light">
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">AGS</th>
                <th className="px-3 py-2 text-right font-medium text-gut8-anthracite">Score</th>
                <th className="px-3 py-2 text-left font-medium text-gut8-anthracite">Text-Label</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className="border-b border-border-gut8-light last:border-b-0">
                  <td className="px-3 py-2 text-gut8-anthracite">{r.entity_name}</td>
                  <td className="px-3 py-2 text-text-muted font-mono text-xs">{r.entity_ags}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.score != null ? r.score.toFixed(2) : '\u2014'}
                  </td>
                  <td className="px-3 py-2 text-text-muted">{r.text_label ?? '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {results.length === 0 && errors.length === 0 && !isPreviewing && (
        <p className="text-sm text-text-muted text-center py-4">
          Klicken Sie auf &quot;Testen&quot; um eine Vorschau der Berechnung zu sehen.
        </p>
      )}
    </div>
  )
}
